import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { patchCachedRow, restoreCachedRows } from '../../lib/optimistic';
import {
  createCampaign,
  createContentItem,
  deleteCampaign,
  deleteContentItem,
  fetchCampaigns,
  fetchContentItems,
  updateCampaign,
  updateContentItem,
} from './queries';
import type { CampaignInput, ContentInput, ContentUpdate } from './queries';
import type { Campaign, ContentItem } from './types';

export const studioKeys = {
  all: ['contentStudio'] as const,
  campaigns: () => [...studioKeys.all, 'campaigns'] as const,
  items: () => [...studioKeys.all, 'items'] as const,
};

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useCampaigns() {
  const { client } = useAuth();
  return useQuery({
    queryKey: studioKeys.campaigns(),
    queryFn: () => fetchCampaigns(client),
    retry: false,
  });
}

export function useContentItems() {
  const { client } = useAuth();
  return useQuery({
    queryKey: studioKeys.items(),
    queryFn: () => fetchContentItems(client),
    retry: false,
  });
}

export function useCreateContentItem() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: ContentInput) => createContentItem(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: studioKeys.items() });
    },
  });
}

export function useUpdateContentItem() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const updatedBy = useActorId();
  return useMutation({
    mutationFn: ({ itemId, changes }: { itemId: string; changes: ContentUpdate }) =>
      updateContentItem(client, itemId, { ...changes, updated_by: updatedBy }),
    // Optimistic, same as the other boards. updated_by is deliberately NOT
    // patched into the cache: it is the database's answer, not ours, and
    // guessing it would show the wrong name for a moment on a row somebody is
    // looking straight at.
    onMutate: (input) =>
      patchCachedRow<ContentItem>(queryClient, studioKeys.items(), input.itemId, input.changes),
    onError: (_error, _input, context) => {
      restoreCachedRows(queryClient, studioKeys.items(), context);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: studioKeys.items() });
    },
  });
}

export function useDeleteContentItem() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => deleteContentItem(client, itemId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: studioKeys.items() });
    },
  });
}

export function useCreateCampaign() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: CampaignInput) => createCampaign(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: studioKeys.campaigns() });
    },
  });
}

export function useUpdateCampaign() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      changes,
    }: {
      campaignId: string;
      changes: Parameters<typeof updateCampaign>[2];
    }) => updateCampaign(client, campaignId, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: studioKeys.campaigns() });
    },
  });
}

export function useDeleteCampaign() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => deleteCampaign(client, campaignId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: studioKeys.campaigns() });
      void queryClient.invalidateQueries({ queryKey: studioKeys.items() });
    },
  });
}

/** Whether the signed-in person may approve (mirrors the database guard). */
export function useIsStudioAdmin(): boolean {
  const { access } = useAuth();
  return access.status === 'approved' && access.profile.internal_role === 'tap_admin';
}

export type { Campaign };
